import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Mail, MailOpen, Phone, Car, MapPin, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardMessages() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadMessages();
  }, [navigate]);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/contact-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Errore nel caricamento dei messaggi');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (msgId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/contact-messages/${msgId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.map(m => m.id === msgId ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-messages">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>Messaggi Contatto</h1>
            <p className="text-gray-600">Messaggi ricevuti dal form di contatto del sito</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="messages-loading" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200" data-testid="messages-empty">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nessun messaggio ricevuto</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all ${msg.read ? 'border-gray-100' : 'border-[#0066b2]/30 shadow-md'}`}
                  data-testid={`message-item-${idx}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {msg.read ? (
                          <MailOpen className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Mail className="w-5 h-5 text-[#0066b2]" />
                        )}
                        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>{msg.full_name}</h3>
                        {!msg.read && (
                          <span className="px-2 py-0.5 bg-[#0066b2] text-white text-xs rounded-full font-medium">Nuovo</span>
                        )}
                      </div>

                      <p className="text-gray-700 mb-4 whitespace-pre-line">{msg.service_requested}</p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> {msg.email}
                        </span>
                        {msg.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" /> {msg.phone}
                          </span>
                        )}
                        {msg.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {msg.city}
                          </span>
                        )}
                        {msg.car_model && (
                          <span className="flex items-center gap-1">
                            <Car className="w-3.5 h-3.5" /> {msg.car_model} {msg.license_plate && `(${msg.license_plate})`}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" /> {formatDate(msg.created_at)}
                        </span>
                      </div>
                    </div>

                    {!msg.read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(msg.id)}
                        data-testid={`mark-read-${idx}`}
                      >
                        Segna letto
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
